import React from 'react';
import { Box, Typography, Button, Container } from '@mui/material';
import { ArrowForward as ArrowIcon, Code as CodeIcon } from '@mui/icons-material';
import { wolvesLabsColors } from '../../theme/wolvesLabsTheme';

const Hero: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: wolvesLabsColors.gradients.hero,
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Elementos decorativos de fondo */}
      <Box
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-10%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'rgba(59, 130, 246, 0.3)',
          filter: 'blur(100px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '-10%',
          left: '-10%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: 'rgba(30, 58, 138, 0.3)',
          filter: 'blur(100px)',
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box sx={{ textAlign: 'center', py: { xs: 8, md: 12 } }}>
          {/* Logo/Icono */}
          <Box sx={{ mb: 4 }}>
            <CodeIcon 
              sx={{ 
                fontSize: { xs: 80, md: 120 }, 
                color: wolvesLabsColors.tech,
                filter: 'drop-shadow(0 0 20px rgba(233, 69, 96, 0.5))',
              }} 
            />
          </Box>

          {/* Título principal */}
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5.5rem' },
              fontWeight: 800,
              mb: 2,
              background: 'linear-gradient(135deg, #ffffff 0%, #e94560 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 0 40px rgba(255,255,255,0.1)',
            }}
          >
            WolvesLabs
          </Typography>

          {/* Subtítulo */}
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.8rem' },
              mb: 4,
              color: 'white !important',
              fontWeight: 400,
              letterSpacing: '0.05em',
              textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
            }}
          >
            Desarrollo de Software a la Medida
          </Typography>

          {/* Descripción */}
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '1rem', md: '1.2rem' },
              mb: 5,
              maxWidth: '800px',
              mx: 'auto',
              opacity: 0.85,
              lineHeight: 1.8,
            }}
          >
            Transformamos tus ideas en soluciones tecnológicas innovadoras. 
            Desarrollo web, móvil, desktop y consultoría especializada para impulsar tu negocio.
          </Typography>

          {/* Call to Actions */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowIcon />}
              onClick={() => scrollToSection('services')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: wolvesLabsColors.gradients.tech,
                boxShadow: '0 8px 30px rgba(233, 69, 96, 0.4)',
                '&:hover': {
                  boxShadow: '0 12px 40px rgba(233, 69, 96, 0.6)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Ver Servicios
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => scrollToSection('contact')}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderColor: 'white',
                color: 'white',
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: wolvesLabsColors.tech,
                  backgroundColor: 'rgba(233, 69, 96, 0.1)',
                },
              }}
            >
              Contactar
            </Button>
          </Box>

          {/* Stats o badges */}
          <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap' }}>
            {[
              { number: '5+', label: 'Años de Experiencia' },
              { number: '50+', label: 'Proyectos Completados' },
              { number: '100%', label: 'Clientes Satisfechos' },
            ].map((stat, index) => (
              <Box key={index} sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    color: 'white !important',
                    mb: 0.5,
                    textShadow: '0 2px 10px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'white !important', 
                    fontSize: '0.9rem',
                    textShadow: '0 1px 5px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      {/* Indicador de scroll */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 30,
          left: '50%',
          transform: 'translateX(-50%)',
          animation: 'bounce 2s infinite',
          '@keyframes bounce': {
            '0%, 100%': {
              transform: 'translateX(-50%) translateY(0)',
            },
            '50%': {
              transform: 'translateX(-50%) translateY(-10px)',
            },
          },
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          Desliza para ver más
        </Typography>
      </Box>
    </Box>
  );
};

export default Hero;

